// Convert height from cm to inches
const cmToInches = (cm: number): number => cm / 2.54;

// Calculate BMI from height (in cm) and weight (in pounds)
export const calculateBMI = (
  heightValue: string,
  weightInPounds: number,
): number => {
  const heightInCm = parseFloat(heightValue);

  // Explicitly check for zero weight, negative values, or invalid inputs
  if (
    isNaN(heightInCm) ||
    isNaN(weightInPounds) ||
    heightInCm <= 0 ||
    weightInPounds <= 0
  ) {
    return 0;
  }

  const heightInInches = cmToInches(heightInCm);
  // BMI formula: (weight in pounds * 703) / (height in inches)²
  return (weightInPounds * 703) / (heightInInches * heightInInches);
};

// Check if BMI is within valid range
export const isValidBMI = (
  bmi: number,
  minBMI?: number,
  maxBMI?: number,
): boolean => {
  // If no range is specified, don't perform validation
  if (minBMI === undefined && maxBMI === undefined) return true;

  // Check against specified bounds
  if (minBMI !== undefined && maxBMI !== undefined) {
    return bmi >= minBMI && bmi <= maxBMI;
  } else if (minBMI !== undefined) {
    return bmi >= minBMI;
  } else if (maxBMI !== undefined) {
    return bmi <= maxBMI;
  }

  return true;
};

// Params for validateBMI function
export interface ValidateBMIParams {
  heightValue: string;
  weightValue: string;
  minBMI?: number;
  maxBMI?: number;
  minWeight?: number; // Minimum weight in lbs
  maxWeight?: number; // Maximum weight in lbs
  onBmiCalculated?: (bmi: number) => void;
}

// Validate BMI using an object of parameters
export const validateBMI = ({
  heightValue,
  weightValue,
  minBMI,
  maxBMI,
  minWeight,
  maxWeight,
  onBmiCalculated,
}: ValidateBMIParams): { isValid: boolean; message?: string; bmi?: number } => {
  const weight = parseFloat(weightValue);

  // Check for empty height (allow this to be valid)
  if (!heightValue) {
    return { isValid: true };
  }

  // Check for invalid weight or zero weight
  if (isNaN(weight)) {
    return { isValid: true };
  }

  // Fix: Return invalid for zero or negative weight with explicit undefined bmi
  if (weight <= 0) {
    return {
      isValid: false,
      message:
        "Weight cannot be zero or negative. Please enter a valid weight.",
      bmi: undefined,
    };
  }

  // Validate weight bounds (independent of BMI calculation)
  if (minWeight !== undefined && weight < minWeight) {
    return {
      isValid: false,
      message: `Weight must be at least ${minWeight} lbs (${(minWeight * 0.453592).toFixed(1)} kg) to be eligible for this study.`,
      bmi: undefined,
    };
  }

  if (maxWeight !== undefined && weight > maxWeight) {
    return {
      isValid: false,
      message: `Weight must be no more than ${maxWeight} lbs (${(maxWeight * 0.453592).toFixed(1)} kg) to be eligible for this study.`,
      bmi: undefined,
    };
  }

  const calculatedBMI = calculateBMI(heightValue, weight);

  // Call the callback if provided
  if (onBmiCalculated) {
    onBmiCalculated(calculatedBMI);
  }

  // If no limits, just calculate
  if (minBMI === undefined && maxBMI === undefined) {
    return { isValid: true, bmi: calculatedBMI };
  }

  // Validate against limits
  if (!isValidBMI(calculatedBMI, minBMI, maxBMI)) {
    // Format BMI values intelligently (show whole numbers as integers, decimals with 1 place)
    const formatBMI = (value: number): string => {
      return Number.isInteger(value) ? value.toString() : value.toFixed(1);
    };
    const formattedBMI = formatBMI(calculatedBMI);

    // Determine what kind of range we're dealing with and craft a helpful message
    let message = "";

    if (minBMI !== undefined && maxBMI !== undefined) {
      // Both min and max are defined
      if (calculatedBMI < minBMI) {
        message = `Your BMI (${formattedBMI}) is below the required range of ${formatBMI(minBMI)}-${formatBMI(maxBMI)} for this study. `;
        message += `You would need a BMI of at least ${formatBMI(minBMI)} to qualify.`;
      } else {
        message = `Your BMI (${formattedBMI}) is above the required range of ${formatBMI(minBMI)}-${formatBMI(maxBMI)} for this study. `;
        message += `You would need a BMI of no more than ${formatBMI(maxBMI)} to qualify.`;
      }
    } else if (minBMI !== undefined) {
      // Only min is defined
      message = `Your BMI (${formattedBMI}) is below the minimum requirement of ${formatBMI(minBMI)} for this study.`;
    } else if (maxBMI !== undefined) {
      // Only max is defined
      message = `Your BMI (${formattedBMI}) exceeds the maximum allowed BMI of ${formatBMI(maxBMI)} for this study.`;
    }

    return { isValid: false, message, bmi: calculatedBMI };
  }

  return { isValid: true, bmi: calculatedBMI };
};

// Type for the BMI message response
export interface BMIMessage {
  text: string;
  isValid: boolean;
}

/**
 * Get a formatted message about BMI status based on requirements
 * @param bmi User's calculated BMI
 * @param minBMI Minimum BMI requirement (optional)
 * @param maxBMI Maximum BMI requirement (optional)
 * @returns Formatted message with validation status
 */
export const getBMIStatusMessage = (
  bmi: number,
  minBMI?: number,
  maxBMI?: number,
): BMIMessage => {
  // Case: Both min and max BMI requirements exist
  if (minBMI !== undefined && maxBMI !== undefined) {
    if (bmi >= minBMI && bmi <= maxBMI) {
      return {
        text: `✓ Your BMI meets the required range (${minBMI}-${maxBMI})`,
        isValid: true,
      };
    } else if (bmi < minBMI) {
      return {
        text: `⚠️ Your BMI is below the required range (${minBMI}-${maxBMI})`,
        isValid: false,
      };
    } else {
      return {
        text: `⚠️ Your BMI is above the required range (${minBMI}-${maxBMI})`,
        isValid: false,
      };
    }
  }
  // Case: Only min BMI requirement exists
  else if (minBMI !== undefined) {
    return bmi >= minBMI
      ? {
          text: `✓ Your BMI meets the minimum requirement of ${minBMI}`,
          isValid: true,
        }
      : {
          text: `⚠️ Your BMI must be at least ${minBMI} to be eligible for this study`,
          isValid: false,
        };
  }
  // Case: Only max BMI requirement exists
  else if (maxBMI !== undefined) {
    return bmi <= maxBMI
      ? {
          text: `✓ Your BMI is below the maximum limit of ${maxBMI}`,
          isValid: true,
        }
      : {
          text: `⚠️ Your BMI must be below ${maxBMI}`,
          isValid: false,
        };
  }

  // No requirements case
  return {
    text: "",
    isValid: true,
  };
};
